import * as THREE from 'three';
import { GridLayout } from './GridLayout.js';
import { SheetState } from '../config/SheetConfig.js';
import { ImageLoader } from './ImageLoader.js';
import { SheetAnimation } from './SheetAnimation.js';
import { ResourceManager, clearExistingImageMeshes } from './ResourceManagement.js';
import { GestureManager } from './GestureManager.js';
import { DataLoader } from '../utils/DataLoader.js';
import { 
    DOUBLE_TAP_THRESHOLD, 
    SWIPE_VELOCITY_THRESHOLD, 
    SWIPE_DISTANCE_THRESHOLD, 
    DRAG_THRESHOLD, 
    CLICK_DISTANCE_THRESHOLD,
    ANIMATION_DURATIONS
} from '../config/SheetConfig.js';
import {
    isOverImage,
    calculateOverviewFrustum,
    calculateZoomFrustum,
    addEventListener as addEventListenerUtil,
    removeEventListeners as removeEventListenersUtil
} from './SheetUtils.js';
import { DetailView } from './DetailView.js';
import { Router } from './Router.js';
import { ContextMenu } from './ContextMenu.js';
import { geometryPool } from '../core/GeometryPool.js';
import { AnimationController } from '../core/AnimationController.js';

export class ContactSheet {
    constructor(scene, camera, sheetId = 'sosf', gradientBackground = null) {
        this.scene = scene;
        this.camera = camera;
        this.layout = new GridLayout();
        this.state = SheetState.OVERVIEW;
        this.sheetId = sheetId;
        this.gradientBackground = gradientBackground;
        
        // Initialize ResourceManager
        this.resourceManager = new ResourceManager();
        
        // Calculate and store current frustum settings - this will be recalculated on resize
        const { size: frustumSize, aspect } = calculateOverviewFrustum();
        const halfHeight = frustumSize / 2;
        const halfWidth = frustumSize * aspect / 2;
        
        // Store original camera settings
        this.originalFrustum = {
            left: -halfWidth,
            right: halfWidth,
            top: halfHeight,
            bottom: -halfHeight
        };
        
        // Interaction state
        this.raycaster = new THREE.Raycaster();
        this.pointer = new THREE.Vector2();
        // currentImage is now a getter that delegates to NavigationController
        
        // Touch state
        this.isDragging = false;
        this.panInProgress = false;
        this.startX = 0;
        this.startY = 0;
        this.lastX = 0;
        this.lastY = 0;
        this.lastTime = 0;
        this.velocityX = 0;
        this.velocityY = 0;
        this.swipeDirection = null;
        this.hasMovedBeyondThreshold = false;
        
        // Event listeners to be cleaned up
        this.eventListeners = [];
        
        // Brightness state management (moved to SheetAnimation)
        
        // Initialize helper classes
        this.imageLoader = new ImageLoader();
        this.animation = new SheetAnimation(camera, gradientBackground);
        this.detailView = new DetailView();
        this.contextMenu = new ContextMenu();
        this.dataLoader = new DataLoader();
        
        // Create close button (same as DetailView)
        this.closeButton = document.createElement('button');
        this.closeButton.className = 'circular-btn circular-btn-close';
        this.closeButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="white"/>
            </svg>
        `;
        this.closeButton.style.cssText = `
            opacity: 0;
            pointer-events: none;
        `;
        this.closeButton.setAttribute('aria-label', 'Close');
        this.closeButton.addEventListener('click', () => {
            console.log('ContactSheet: Close button clicked. Routing to NC.');
            this.navigationController?.navigate('zoom-out');
        });
        document.body.appendChild(this.closeButton);
        
        // Create zoom icon overlay for desktop hover
        this.zoomIconOverlay = document.createElement('div');
        this.zoomIconOverlay.className = 'circular-btn circular-btn-large';
        this.zoomIconOverlay.innerHTML = `
            <svg width="32" height="32" viewBox="0 -960 960 960" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M200-200v-240h80v160h160v80H200Zm480-320v-160H520v-80h240v240h-80Z" fill="white"/>
            </svg>
        `;
        this.zoomIconOverlay.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            z-index: 1001;
            opacity: 0;
            pointer-events: none;
        `;
        document.body.appendChild(this.zoomIconOverlay);

        // Ensure overlay cannot get stuck across app lifecycle events (share sheet, tab switch)
        const hideAllOverlays = () => {
            this.hideZoomIcon();
            this.hideMobileZoomIcon(true);
        };
        document.addEventListener('visibilitychange', hideAllOverlays, { passive: true });
        window.addEventListener('pageshow', hideAllOverlays, { passive: true });
        window.addEventListener('focus', hideAllOverlays, { passive: true });
        // Track for cleanup
        this.eventListeners.push({ element: document, type: 'visibilitychange', handler: hideAllOverlays });
        this.eventListeners.push({ element: window, type: 'pageshow', handler: hideAllOverlays });
        this.eventListeners.push({ element: window, type: 'focus', handler: hideAllOverlays });
        
        // Track if we're on a touch device
        this.isTouchDevice = this.detectTouchDevice();
        
        // Sheet Z position constant
        this.SHEET_Z_POSITION = -2.5;

        // Performance optimizations
        this.meshCache = new Map(); // Direct mesh references to avoid scene.traverse()
        this.isShowingZoomIcon = false; // Prevent redundant zoom icon animations

        // Create tutorial instructions for different states
        this.createTutorialInstructions();
    }
    
    // Main initialization method
    async init() {
        await this.setupSheet();
        await this.createImagesFromSheet();
        this.resetImageBrightness();
        this.setupGestureManager();
        this.setupResizeHandling();
        this.setupContextMenu();
        
        // Show overview tutorial after initial setup (with small delay)
        setTimeout(() => {
            this.showOverviewTutorial();
        }, 1000);
    }
    
    async setupSheet() {
        try {
            const sheetTexture = await this.imageLoader.loadTextureWithProperEncoding('/images/contact-sheet-background.jpg');
            
            const dimensions = this.layout.getSheetDimensions();
            
            const material = new THREE.MeshBasicMaterial({
                map: sheetTexture,
                side: THREE.FrontSide,
                transparent: true,
                opacity: 0
            });
            
            this.sheet = geometryPool.createPlaneMesh(
                dimensions.width, 
                dimensions.height, 
                material, 
                { x: 0, y: 0, z: -2.5 }
            );
            
            this.SHEET_Z_POSITION = -2.5;
            this.scene.add(this.sheet);

            // Wait one render frame so the texture is uploaded to GPU before fading in
            await new Promise(resolve => requestAnimationFrame(resolve));

            gsap.to(material, {
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out'
            });

            
        } catch (error) {
            console.error('Failed to load contact sheet background, using fallback:', error);
            // Create a fallback sheet without texture
            const dimensions = this.layout.getSheetDimensions();
            const material = new THREE.MeshBasicMaterial({
                color: 0x1a1a1a,
                side: THREE.FrontSide
            });
            
            this.sheet = geometryPool.createPlaneMesh(
                dimensions.width, 
                dimensions.height, 
                material, 
                { x: 0, y: 0, z: -2.5 }
            );
            
            this.SHEET_Z_POSITION = -2.5;
            this.scene.add(this.sheet);
        }
    }
    
    
    setupGestureManager() {
        const canvas = document.querySelector('canvas');
        if (!canvas) return;
        
        this.gestureManager = new GestureManager(canvas, {
            onTap: (event) => {
                // Route through NavigationController if available
                if (this.navigationController) {
                    this.navigationController.handleTap(event);
                } else {
                    this.handleTap(event);
                }
            },
            // Intentionally omit onDoubleTap to avoid re-entrant transitions
            onPanStart: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePanStart(event);
                } else {
                    this.handlePanStart(event);
                }
            },
            onPanMove: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePanMove(event);
                } else {
                    this.handlePanMove(event);
                }
            },
            onPanEnd: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePanEnd(event);
                } else {
                    this.handlePanEnd(event);
                }
            },
            onSwipe: (event) => {
                if (this.navigationController) {
                    this.navigationController.handleSwipe(event);
                } else {
                    this.handleSwipe(event);
                }
            },
            onPinchStart: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePinchStart(event);
                } else {
                    this.handlePinchStart(event);
                }
            },
            onPinchIn: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePinchIn(event);
                } else {
                    this.handlePinchIn(event);
                }
            },
            onPinchOut: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePinchOut(event);
                } else {
                    this.handlePinchOut(event);
                }
            },
            onPinchEnd: (event) => {
                if (this.navigationController) {
                    this.navigationController.handlePinchEnd(event);
                } else {
                    this.handlePinchEnd(event);
                }
            },
            onMultiTouchChange: (isMultiTouch) => {
                if (this.navigationController) {
                    this.navigationController.handleMultiTouchChange(isMultiTouch);
                } else {
                    this.handleMultiTouchChange(isMultiTouch);
                }
            }
        });
        
        // Set cursor styles
        this.setupCursorStyles(canvas);
        

    }
    
    setupCursorStyles(canvas) {
        // Handle cursor style and zoom icon hover (desktop only)
        addEventListenerUtil(canvas, 'mousemove', (event) => {
            if (this.state === SheetState.ANIMATING || this.isTouchDevice) return;
            
            this.updatePointerPosition(event);
            const intersects = this.raycaster.intersectObject(this.sheet);
            
            if (intersects.length > 0 && isOverImage(intersects[0].uv, this.layout)) {
                canvas.style.cursor = 'pointer';
                
                // Show zoom icon if we're zoomed in and hovering over the current focused image
                if (this.state === SheetState.ZOOMED_IN) {
                    const image = this.getImageAtPointer();
                    if (image && image.row === this.currentImage.row && image.col === this.currentImage.col) {
                        this.showZoomIcon();
                    } else {
                        this.hideZoomIcon();
                    }
                }
            } else {
                canvas.style.cursor = 'default';
                if (this.state === SheetState.ZOOMED_IN) {
                    this.hideZoomIcon();
                }
            }
        }, this.eventListeners);
        
        addEventListenerUtil(canvas, 'mouseleave', () => {
            canvas.style.cursor = 'default';
            if (this.state === SheetState.ZOOMED_IN && !this.isTouchDevice) {
                this.hideZoomIcon();
            }
        }, this.eventListeners);
    }

    
    setupResizeHandling() {
        // Handle resize events for camera adjustment
        addEventListenerUtil(window, 'resize', () => {
            this.handleResize();
        }, this.eventListeners);
    }
    
    setupContextMenu() {
        // Pass layout information to context menu
        this.contextMenu.setLayoutInfo({
            rows: this.layout.rows,
            columns: this.layout.columns
        });
        
        // Wire context menu to NavigationController if available
        if (this.navigationController) {
            this.contextMenu.setNavigationController(this.navigationController);
        }
        // Remove legacy navigation callbacks. All navigation must go through NavigationController.
        // Only set callbacks here if they are non-navigation (none currently needed).
        // Update context menu state when our state changes
        this.updateContextMenuState();
    }
    
    navigateToAdjacentImage(direction, targetPosition = null) {
        // Use precalculated position from NavigationController if provided
        if (targetPosition) {
            this.moveToImage(targetPosition);
            return;
        }
        
        // Legacy fallback for direct calls (deprecated)
        console.warn('ContactSheet.navigateToAdjacentImage called without targetPosition. Use NavigationController.navigate() instead.');
        let targetRow = this.currentImage.row;
        let targetCol = this.currentImage.col;
        let isValidMove = true;
        
        switch (direction) {
            case 'left':
                targetCol = this.currentImage.col - 1;
                isValidMove = targetCol >= 0;
                break;
            case 'right':
                targetCol = this.currentImage.col + 1;
                isValidMove = targetCol < this.layout.columns;
                break;
            case 'up':
                targetRow = this.currentImage.row - 1;
                isValidMove = targetRow >= 0;
                break;
            case 'down':
                targetRow = this.currentImage.row + 1;
                isValidMove = targetRow < this.layout.rows;
                break;
        }
        
        if (isValidMove) {
            const targetImage = { row: targetRow, col: targetCol };
            this.moveToImage(targetImage);
        } else {
            // Show bounce animation
            const keyMap = {
                'left': 'ArrowLeft',
                'right': 'ArrowRight',
                'up': 'ArrowUp',
                'down': 'ArrowDown'
            };
            this.showBounceAnimation(keyMap[direction]);
        }
    }
    
    // navigateWithWrapping moved to NavigationController for consistency
    
    updateContextMenuState() {
        // Update context menu with current view state
        if (this.state === SheetState.OVERVIEW) {
            this.contextMenu.setView('overview');
        } else if (this.state === SheetState.ZOOMED_IN) {
            this.contextMenu.setView('zoomed');
        } else if (this.state === SheetState.DETAIL_VIEW) {
            this.contextMenu.setView('detail');
        }
        
        // Set current image data with actual filename from imageMapping
        const filename = this.imageMapping && 
                        this.imageMapping[this.currentImage.row] && 
                        this.imageMapping[this.currentImage.row][this.currentImage.col] 
                        ? this.imageMapping[this.currentImage.row][this.currentImage.col]
                        : `image_${this.currentImage.row}_${this.currentImage.col}`;
                        
        this.contextMenu.setImageData({
            row: this.currentImage.row,
            col: this.currentImage.col,
            filename: filename
        });
    }
    
    handleTap(event) {
        if (this.state === SheetState.ANIMATING) return;
        
        // Don't process tap if we just finished a pan
        if (this.panInProgress) return;
        
        // Convert touch coordinates to Three.js coordinates
        this.updatePointerFromTouch(event.center);
        
        if (this.state === SheetState.OVERVIEW) {
            // Don't mark overview tutorial as learned yet - let it show first
            
            // When in overview state, tapping zooms in to an image
            // Route through NavigationController for unified handling
            let image = this.getImageAtPointer();
            
            // If no direct image hit, find the nearest image to the tap point
            if (!image) {
                image = this.findNearestImageToPoint(event.center);
            }
            
            if (image) {
                
                console.log(`ContactSheet: Tap on image (${image.row},${image.col}). Routing to NC.`);
                this.navigationController?.navigate('zoom-to-image', { 
                    position: { row: image.row, col: image.col },
                    source: 'tap'
                });
            }
        } else if (this.state === SheetState.ZOOMED_IN) {
            // Mark zoomed tutorial as learned on interaction within zoomed state
            this.markTutorialLearned('zoomed');
            
            // When zoomed in, tap handles image interactions
            this.handleImageTap(event);
        }
    }
    
    handlePanStart(event) {
        // GestureManager handles multi-touch detection
        
        if (this.state === SheetState.ANIMATING) return;
        
        if (this.state === SheetState.ZOOMED_IN) {
            // Hide mobile zoom icon instantly when pan starts
            this.hideMobileZoomIcon(true);
            
            // Trigger pan immediately for single touch
            this.isDragging = true;
            this.hasMovedBeyondThreshold = false;
            this.panInProgress = true;
            this.startX = event.center.x;
            this.startY = event.center.y;
            this.lastX = this.startX;
            this.lastY = this.startY;
            this.lastTime = performance.now();
            this.velocityX = 0;
            this.velocityY = 0;
            this.swipeDirection = null;
            
            const canvas = document.querySelector('canvas');
            if (canvas) {
                canvas.style.cursor = 'grabbing';
            }
        }
    }
    
    handlePanMove(event) {
        // GestureManager prevents pan during multi-touch
        
        if (!this.isDragging || this.state !== SheetState.ZOOMED_IN || this.state === SheetState.ANIMATING) return;
        
        // Calculate distance moved to distinguish between click and drag
        if (!this.hasMovedBeyondThreshold) {
            const deltaX = Math.abs(event.center.x - this.startX);
            const deltaY = Math.abs(event.center.y - this.startY);
            
            if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
                this.hasMovedBeyondThreshold = true;
            }
        }
        
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastTime;
        
        // Smooth velocity tracking
        const rawVelocityX = (event.center.x - this.lastX) / deltaTime;
        const rawVelocityY = (event.center.y - this.lastY) / deltaTime;
        
        // Apply exponential smoothing for stability
        const smoothing = 0.3;
        this.velocityX = this.velocityX * (1 - smoothing) + rawVelocityX * smoothing;
        this.velocityY = this.velocityY * (1 - smoothing) + rawVelocityY * smoothing;
        
        if (!this.swipeDirection) {
            const deltaX = Math.abs(event.center.x - this.startX);
            const deltaY = Math.abs(event.center.y - this.startY);
            // Wait for meaningful movement before locking direction
            // to avoid noisy initial events misclassifying the gesture
            if (deltaX > 8 || deltaY > 8) {
                this.swipeDirection = deltaX >= deltaY ? 'horizontal' : 'vertical';
            } else {
                return; // Too early to determine direction — skip this frame
            }
        }
        
        const scale = (this.camera.top - this.camera.bottom) / window.innerHeight;
        const deltaX = (event.center.x - this.lastX) * scale;
        const deltaY = (event.center.y - this.lastY) * scale;
        
        if (this.swipeDirection === 'horizontal') {
            // Apply boundary resistance when at edges
            const currentImagePos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
            const leftBoundary = this.currentImage.col > 0 ? 
                this.layout.getImagePosition(this.currentImage.row, this.currentImage.col - 1).x : currentImagePos.x;
            const rightBoundary = this.currentImage.col < this.layout.columns - 1 ? 
                this.layout.getImagePosition(this.currentImage.row, this.currentImage.col + 1).x : currentImagePos.x;
            
            const newX = this.camera.position.x - deltaX;
            let resistance = 1;
            
            // Apply resistance if beyond boundaries
            if (newX < leftBoundary || newX > rightBoundary) {
                resistance = 0.25; // iOS-like resistance
            }
            
            const adjustedDeltaX = deltaX * resistance;
            this.camera.position.x -= adjustedDeltaX;
            
            if (this.gradientBackground) {
                const parallaxFactor = 0.15;
                this.gradientBackground.position.x -= adjustedDeltaX * parallaxFactor;
            }
        } else {
            // Apply boundary resistance for vertical
            const currentImagePos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
            const topBoundary = this.currentImage.row > 0 ? 
                this.layout.getImagePosition(this.currentImage.row - 1, this.currentImage.col).y : currentImagePos.y;
            const bottomBoundary = this.currentImage.row < this.layout.rows - 1 ? 
                this.layout.getImagePosition(this.currentImage.row + 1, this.currentImage.col).y : currentImagePos.y;
            
            const newY = this.camera.position.y + deltaY;
            let resistance = 1;
            
            // Apply resistance if beyond boundaries
            if (newY > topBoundary || newY < bottomBoundary) {
                resistance = 0.25; // iOS-like resistance
            }
            
            const adjustedDeltaY = deltaY * resistance;
            this.camera.position.y += adjustedDeltaY;
            
            if (this.gradientBackground) {
                const parallaxFactor = 0.15;
                this.gradientBackground.position.y += adjustedDeltaY * parallaxFactor;
            }
        }
        
        this.lastX = event.center.x;
        this.lastY = event.center.y;
        this.lastTime = currentTime;
    }
    
    handlePanEnd(event) {
        // GestureManager prevents pan end during multi-touch
        
        if (!this.isDragging || this.state !== SheetState.ZOOMED_IN || this.state === SheetState.ANIMATING) return;
        
        if (this.isPinching) return;
        
        this.isDragging = false;
        
        const canvas = document.querySelector('canvas');
        if (canvas) {
            this.updatePointerFromTouch(event.center);
            const intersects = this.raycaster.intersectObject(this.sheet);
            canvas.style.cursor = (intersects.length > 0 && isOverImage(intersects[0].uv, this.layout)) 
                ? 'pointer' 
                : 'default';
        }
        
        if (!this.hasMovedBeyondThreshold) {
            // Clear pan flag for small movements too
            setTimeout(() => { this.panInProgress = false; }, 100);
            return;
        }
        
        const totalDeltaX = event.center.x - this.startX;
        const totalDeltaY = event.center.y - this.startY;
        
        let targetImage = { ...this.currentImage };
        
        if (this.swipeDirection === 'horizontal') {
            // Check if there's any horizontal movement
            if (Math.abs(totalDeltaX) > 1) { // Very small threshold just to avoid noise
                if (this.velocityX > 0 || totalDeltaX > 0) {
                    // Moving right (to previous column)
                    const newCol = this.currentImage.col - 1;
                    if (newCol >= 0) {
                        targetImage.col = newCol;
                    }
                    // If newCol < 0, targetImage stays the same (spring back to current)
                } else {
                    // Moving left (to next column)
                    const newCol = this.currentImage.col + 1;
                    if (newCol < this.layout.columns) {
                        targetImage.col = newCol;
                    }
                    // If newCol >= columns, targetImage stays the same (spring back to current)
                }
            }
        } else {
            // Check if there's any vertical movement
            if (Math.abs(totalDeltaY) > 1) { // Very small threshold just to avoid noise
                if (this.velocityY > 0 || totalDeltaY > 0) {
                    // Moving down (to previous row)
                    const newRow = this.currentImage.row - 1;
                    if (newRow >= 0) {
                        targetImage.row = newRow;
                    }
                    // If newRow < 0, targetImage stays the same (spring back to current)
                } else {
                    // Moving up (to next row)
                    const newRow = this.currentImage.row + 1;
                    if (newRow < this.layout.rows) {
                        targetImage.row = newRow;
                    }
                    // If newRow >= rows, targetImage stays the same (spring back to current)
                }
            }
        }
        
        // Apply momentum or spring back to current image
        // Determine if we're actually changing images
        const isImageChange = targetImage.row !== this.currentImage.row || targetImage.col !== this.currentImage.col;
        
        if (isImageChange) {
            console.log('ContactSheet: Swipe gesture ended. Routing to NC.');
            // Route through NavigationController for unified navigation
            this.navigationController?.navigateToPosition(targetImage.row, targetImage.col);
        } else {
            // Spring back to current image if no change
            this.moveToImageWithMomentum(targetImage);
        }
        
        // Clear pan flag after a delay to allow legitimate taps
        setTimeout(() => { this.panInProgress = false; }, 100);
    }
    
    handleSwipe(event) {
        // The Pan handlers already handle this behavior more precisely
    }
    
    handleMultiTouchChange(isMultiTouch) {
        // Cancel any ongoing pan if multi-touch starts
        if (isMultiTouch && this.isDragging) {
            this.isDragging = false;
            this.hasMovedBeyondThreshold = false;
            
            // Cancel any pending brightness timers to prevent conflicts
            this.animation.dispose();
            
            // Reset camera position if needed to prevent visual glitches
            if (this.state === SheetState.ZOOMED_IN && this.currentImage) {
                const imagePos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
                gsap.to(this.camera.position, {
                    x: imagePos.x,
                    y: imagePos.y,
                    duration: 0.1,
                    ease: "power1.out",
                    overwrite: 'auto'
                });
            }
        }
    }
    
    moveToImage(targetImage) {
        return new Promise(resolve => {
            const imagePos = this.layout.getImagePosition(targetImage.row, targetImage.col);
            this.state = SheetState.ANIMATING;
            if (this.navigationController) this.navigationController.isTransitioning = true;

            // Hide mobile zoom icon instantly during transition
            this.hideMobileZoomIcon(true);

            const onComplete = () => {
                const currentPos = this.navigationController?.getCurrentPosition();
                if (!currentPos || currentPos.row !== targetImage.row || currentPos.col !== targetImage.col) {
                    this.navigationController?.updatePosition(targetImage.row, targetImage.col);
                }
                this.setImageBrightness(targetImage.row, targetImage.col);
                this.state = SheetState.ZOOMED_IN;
                if (this.navigationController) this.navigationController.isTransitioning = false;
                
                // Show mobile zoom icon over new active image if not learned
                setTimeout(() => {
                    this.showMobileZoomIcon();
                }, 300);
                
                // Update context menu to reflect new position
                this.updateContextMenuState();
                resolve();
            };

            this.animation.moveToAdjacentImage(imagePos, onComplete);
        });
    }
    
    // Enhanced movement with momentum and spring-back
    moveToImageWithMomentum(targetImage) {
        const targetPos = this.layout.getImagePosition(targetImage.row, targetImage.col);
        const currentPos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
        
        this.state = SheetState.ANIMATING;
        if (this.navigationController) this.navigationController.isTransitioning = true;
        
        // Determine if we're actually changing images or need to spring back
        const isImageChange = targetImage.row !== this.currentImage.row || targetImage.col !== this.currentImage.col;
        
        if (isImageChange) {
            // Moving to new image - use existing animation but add subtle momentum
            const velocityMagnitude = Math.sqrt(this.velocityX * this.velocityX + this.velocityY * this.velocityY);
            const momentumFactor = Math.min(velocityMagnitude * 0.1, 0.15); // Cap momentum effect
            
            // Slightly adjust animation timing based on momentum
            const baseDuration = 0.3;
            const adjustedDuration = Math.max(0.2, baseDuration - momentumFactor);
            
            gsap.killTweensOf(this.camera.position);
            gsap.to(this.camera.position, {
                x: targetPos.x,
                y: targetPos.y,
                duration: adjustedDuration,
                ease: "power2.out",
                overwrite: 'auto',
                onComplete: () => {
                    const currentPos = this.navigationController?.getCurrentPosition();
                    if (!currentPos || currentPos.row !== targetImage.row || currentPos.col !== targetImage.col) {
                        this.navigationController?.updatePosition(targetImage.row, targetImage.col);
                    }
                    this.setImageBrightness(targetImage.row, targetImage.col);
                    
                    this.state = SheetState.ZOOMED_IN;
                    if (this.navigationController) this.navigationController.isTransitioning = false;
                    // Update context menu to reflect new position
                    this.updateContextMenuState();
                }
            });
            
            if (this.gradientBackground) {
                const parallaxFactor = 0.15;
                gsap.to(this.gradientBackground.position, {
                    x: targetPos.x * parallaxFactor,
                    y: targetPos.y * parallaxFactor,
                    duration: adjustedDuration,
                    ease: "power2.out",
                    overwrite: 'auto'
                });
            }
        } else {
            // Spring back to current image with smooth ease-out
            gsap.killTweensOf(this.camera.position);
            gsap.to(this.camera.position, {
                x: currentPos.x,
                y: currentPos.y,
                duration: 0.3,
                ease: "power2.out",
                overwrite: 'auto',
                onComplete: () => {
                    this.setImageBrightness(this.currentImage.row, this.currentImage.col);
                    
                    this.state = SheetState.ZOOMED_IN;
                    if (this.navigationController) this.navigationController.isTransitioning = false;
                }
            });
            
            if (this.gradientBackground) {
                const parallaxFactor = 0.15;
                gsap.to(this.gradientBackground.position, {
                    x: currentPos.x * parallaxFactor,
                    y: currentPos.y * parallaxFactor,
                    duration: 0.3,
                    ease: "power2.out",
                    overwrite: 'auto'
                });
            }
        }
    }
    
    // Handle tapping an image when zoomed in
    handleImageTap(event) {
        const intersects = this.raycaster.intersectObject(this.sheet);
        
        // If clicking outside the contact sheet entirely, zoom out
        if (intersects.length === 0) {
            console.log('ContactSheet: Background click. Routing to NC.');
            this.navigationController?.navigate('zoom-out');
            return;
        }
        
        let image = this.getImageAtPointer();
        
        // If no direct image hit, find the nearest image to the tap point
        if (!image) {
            image = this.findNearestImageToPoint(event.center);
        }
        
        // If no image found (either no direct hit or nearest image too far), do nothing
        if (!image) {
            return;
        }
        
        // If tapping the current image, show detail view
        if (image.row === this.currentImage.row && image.col === this.currentImage.col) {
            // Hide mobile zoom icon instantly when tapping to detail
            this.hideMobileZoomIcon(true);
            console.log('ContactSheet: Current image tap. Routing to NC.');
            this.navigationController?.navigate('detail-view');
            return;
        }
        
        // Route adjacent image taps through NavigationController
        console.log('ContactSheet: Adjacent image tap. Routing to NC.');
        this.navigationController?.navigateToPosition(image.row, image.col);
    }
    
    // Update pointer position from a touch event (for Hammer.js)
    updatePointerFromTouch(touchPoint) {
        this.pointer.x = (touchPoint.x / window.innerWidth) * 2 - 1;
        this.pointer.y = -(touchPoint.y / window.innerHeight) * 2 + 1;
        this.raycaster.setFromCamera(this.pointer, this.camera);
    }
    
    // Handle initial zoom into an image
    handleInitialZoom(event) {
        let image = this.getImageAtPointer();
        
        // If no direct image hit, find the nearest image to the tap point
        if (!image) {
            image = this.findNearestImageToPoint(event.center);
        }
        
        if (image) {
            const imagePos = this.layout.getImagePosition(image.row, image.col);
            this.zoomToImage(imagePos, image.row, image.col);
            
            // No scheduled brightness - let zoomToImage animation handle it
        }
    }
    
    // Consolidated cleanup/dispose method
    dispose() {
        this.removeEventListeners();
        
        // Clean up any timers in SheetAnimation
        if (this.animation) {
            this.animation.dispose();
        }
        
        if (this.gestureManager) {
            this.gestureManager.dispose();
        }
        
        // Clean up gesture manager
        const canvas = document.querySelector('canvas');
        if (canvas) {
            // GestureManager handles its own cleanup
        }
        
        this.resourceManager.disposeThreeJsObjects(this.scene, this.sheet, this.SHEET_Z_POSITION);
        
        // Clean up shadow
        if (this.shadow) {
            if (this.shadow.material && this.shadow.material.map) {
                this.shadow.material.map.dispose();
            }
            if (this.shadow.material) {
                this.shadow.material.dispose();
            }
            this.scene.remove(this.shadow);
        }
        
        AnimationController.killTweensOf(this.camera);
        AnimationController.killTweensOf(this.camera.position);

        if (this.detailView) {
            this.detailView.dispose();
        }
        
        // Clean up context menu
        if (this.contextMenu) {
            this.contextMenu.dispose();
        }
        
        // Remove close button
        if (this.closeButton && this.closeButton.parentNode) {
            this.closeButton.parentNode.removeChild(this.closeButton);
        }
        
        // Remove zoom icon overlay
        if (this.zoomIconOverlay && this.zoomIconOverlay.parentNode) {
            this.zoomIconOverlay.parentNode.removeChild(this.zoomIconOverlay);
        }
        
        // Remove tutorial element
        if (this.tutorialElement && this.tutorialElement.parentNode) {
            this.tutorialElement.parentNode.removeChild(this.tutorialElement);
        }
        
        // Clear the texture cache to free memory
        if (this.imageLoader) {
            this.imageLoader.clearCache();
        }
        
        // Clean up mesh cache to prevent memory leaks
        if (this.meshCache) {
            this.meshCache.clear();
        }
    }
    
    // Split out event listener cleanup for clarity
    removeEventListeners() {
        this.eventListeners = removeEventListenersUtil(this.eventListeners);
    }
    
    // Add passive option for touch and wheel events
    addEventListener(element, type, handler) {
        addEventListenerUtil(element, type, handler, this.eventListeners);
    }
    
    updatePointerPosition(event) {
        this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
        this.raycaster.setFromCamera(this.pointer, this.camera);
    }
    
    getImageAtPointer() {
        const intersects = this.raycaster.intersectObject(this.sheet);
        if (intersects.length === 0) return null;
        
        const uv = intersects[0].uv;
        if (!isOverImage(uv, this.layout)) return null;
        
        const gridX = Math.floor((uv.x * this.layout.sheetWidth - this.layout.firstImageX) / (this.layout.imageWidth + this.layout.horizontalMargin));
        const gridY = Math.floor(((1 - uv.y) * this.layout.sheetHeight - this.layout.firstImageY) / (this.layout.imageHeight + this.layout.verticalMargin));
        
        return { row: gridY, col: gridX };
    }
    
    // Update detail view to show new image with smooth transition
    async updateDetailViewToNewImage(targetImage, direction, keyPressed) {
        // Update the DetailView's internal state for linear navigation.
        const newLinearIndex = targetImage.row * this.layout.columns + targetImage.col;
        this.detailView.navigationState.currentLinearIndex = newLinearIndex;

        // Update the context menu with the new image's data.
        this.contextMenu.setImageData({
            row: targetImage.row,
            col: targetImage.col,
            filename: this.imageMapping[targetImage.row][targetImage.col]
        });

        // Get the data for the new image that needs to be displayed.
        const filename = this.imageMapping[targetImage.row][targetImage.col];
        const imageUrl = `/images/${this.sheetId}/${filename}`;
        
        // Log for debugging, as per the PRD.
        if (Router && Router.LOGGING_ENABLED) {
            console.log(`Router 8.3.3: DetailView updating to imageName=${filename}, imageUrl=${imageUrl}`);
        }

        // Move contact sheet position behind the detail view immediately
        this.moveToImage(targetImage);

        // Attempt to get cached image synchronously for instant transition
        // This fixes the "snap" issue by allowing animation to start immediately
        let domImage = this.imageLoader.getCachedDOMImage(imageUrl);

        // Prepare image data object
        const imageData = {
            filename: filename,
            url: imageUrl,
            domImage: domImage,
            metadata: null // Will be updated asynchronously
        };

        // Trigger transition IMMEDIATELY - do not await metadata or image loading
        this.detailView.transitionToNewImage(imageData, direction, keyPressed);
        
        // Update positioning data immediately for potential close animation
        const worldPos = this.layout.getImagePosition(targetImage.row, targetImage.col);
        const imageDimensions = this.layout.getImageDimensions();
        const screenPos = this.worldToScreenCoordinates(worldPos);
        const screenSize = this.worldToScreenSize(imageDimensions);
        
        this.detailView.positioningData = {
            startPosition: screenPos,
            startSize: screenSize,
            endPosition: screenPos,
            endSize: screenSize
        };

        // Load metadata in background and update the shared object
        // DetailView uses the reference, so updating properties works if done quickly (before animation callback)
        this.dataLoader.loadSheetData(this.sheetId)
            .then(sheetData => {
                const meta = sheetData.images.find(img => img.filename === filename);
                if (meta) {
                    imageData.metadata = meta;
                }
            })
            .catch(error => {
                console.warn('Failed to load image metadata for transition:', error);
            });
        
        // Ensure image is cached for next time if it wasn't already
        if (!domImage) {
            this.imageLoader.getDOMImageFromTexture(imageUrl)
                .then(img => {
                    if (!imageData.domImage) {
                        imageData.domImage = img;
                    }
                })
                .catch(error => {
                    console.error(`Failed to load image for detail view transition: ${imageUrl}`, error);
                });
        }
    }
    
    async showDetailView() {
        if (this.state !== SheetState.ZOOMED_IN) return;
        const filename = this.imageMapping[this.currentImage.row][this.currentImage.col];
        const imageUrl = `/images/${this.sheetId}/${filename}`;
        if (Router && Router.LOGGING_ENABLED) {
            console.log(`Router 8.3.3: DetailView opening with imageName=${filename}, imageUrl=${imageUrl}`);
        }
        
        // Get full image metadata from JSON
        let imageMetadata = null;
        try {
            const sheetData = await this.dataLoader.loadSheetData(this.sheetId);
            imageMetadata = sheetData.images.find(img => img.filename === filename);
        } catch (error) {
            console.warn('Failed to load image metadata:', error);
        }
        // Calculate the exact screen position and size of the current focused image
        const worldPos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
        const imageDimensions = this.layout.getImageDimensions();
        
        // Convert world position to screen coordinates
        const screenPos = this.worldToScreenCoordinates(worldPos);
        const screenSize = this.worldToScreenSize(imageDimensions);
        
        // Kill any existing close button tweens and hide contact sheet close button
        gsap.killTweensOf(this.closeButton);
        gsap.to(this.closeButton, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => { this.closeButton.style.pointerEvents = 'none'; }
        });
        
        try {
            // Get the cached DOM image first to eliminate loading jank
            const img = await this.imageLoader.getDOMImageFromTexture(imageUrl);

            // Create image data with the preloaded DOM image and metadata
            const imageData = {
                filename: filename,
                url: imageUrl,
                domImage: img,
                metadata: imageMetadata
            };
            
            // Show the detail view with the cached image and precise positioning
            await this.detailView.show(imageData, this.camera, () => {
                // Detail view closed callback - NavigationController handles close button
                this.transitionToZoomedIn();
            }, () => {
                // Provide correct ZOOMED_IN frustum values for closing
                const { size, aspect } = calculateZoomFrustum();
                const halfSize = size / 2;
                return {
                    left: -halfSize * aspect,
                    right: halfSize * aspect,
                    top: halfSize,
                    bottom: -halfSize
                };
            }, {
                startPosition: screenPos,
                startSize: screenSize,
                endPosition: screenPos,
                endSize: screenSize
            }, this.currentImage, (targetGridPos, keyPressed) => {
                // Navigation callback for mobile linear navigation
                this.updateDetailViewToNewImage(targetGridPos, 'horizontal', keyPressed);
            });
            
            // Set state to DETAIL_VIEW after successful show
            this.transitionToDetailView();

        } catch (error) {
            // Fallback to basic image data if cached version fails
            const imageData = {
                filename: filename,
                url: imageUrl,
                metadata: imageMetadata
            };
            
            // Calculate positioning even for fallback
            const worldPos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
            const imageDimensions = this.layout.getImageDimensions();
            const screenPos = this.worldToScreenCoordinates(worldPos);
            const screenSize = this.worldToScreenSize(imageDimensions);
            
            await this.detailView.show(imageData, this.camera, () => {
                // Detail view closed callback - NavigationController handles close button
                this.transitionToZoomedIn();
            }, () => {
                // Provide correct ZOOMED_IN frustum values for closing
                const { size, aspect } = calculateZoomFrustum();
                const halfSize = size / 2;
                return {
                    left: -halfSize * aspect,
                    right: halfSize * aspect,
                    top: halfSize,
                    bottom: -halfSize
                };
            }, {
                startPosition: screenPos,
                startSize: screenSize,
                endPosition: screenPos,
                endSize: screenSize
            }, this.currentImage, (targetGridPos, keyPressed) => {
                // Navigation callback for mobile linear navigation
                this.updateDetailViewToNewImage(targetGridPos, 'horizontal', keyPressed);
            });
            
            // Set state to DETAIL_VIEW after successful show
            this.transitionToDetailView();
            
            // Update context menu to detail view state
            this.contextMenu.setView('detail');
            this.contextMenu.setImageData({
                filename: filename,
                url: imageUrl,
                row: this.currentImage.row,
                col: this.currentImage.col
            });
        }
    }
    
    // Convert world coordinates to screen coordinates
    worldToScreenCoordinates(worldPos) {
        // Calculate screen position based on camera view
        const screenX = ((worldPos.x - this.camera.position.x) / (this.camera.right - this.camera.left)) * window.innerWidth + (window.innerWidth / 2);
        const screenY = (-(worldPos.y - this.camera.position.y) / (this.camera.top - this.camera.bottom)) * window.innerHeight + (window.innerHeight / 2);
        
        return { x: screenX, y: screenY };
    }
    
    // Convert world size to screen size
    worldToScreenSize(worldDimensions) {
        const screenWidth = (worldDimensions.width / (this.camera.right - this.camera.left)) * window.innerWidth;
        const screenHeight = (worldDimensions.height / (this.camera.top - this.camera.bottom)) * window.innerHeight;
        
        return { width: screenWidth, height: screenHeight };
    }
    
    // Set NavigationController reference
    setNavigationController(navigationController) {
        this.navigationController = navigationController;
        // NavigationController already initializes with position (0,0), no sync needed
        
        // Re-setup context menu now that NavigationController is available
        this.setupContextMenu();
    }

    // Get current position from NavigationController (single source of truth)
    get currentImage() {
        return this.navigationController?.getCurrentPosition() || { row: 0, col: 0 };
    }

    // Deprecated: Use NavigationController.updatePosition() instead
    set currentImage(position) {
        console.warn('ContactSheet.currentImage setter is deprecated. Use NavigationController.updatePosition() instead.');
        this.navigationController?.updatePosition(position.row, position.col);
    }

    // Handle window resize
    handleResize() {
        const { size: frustumSize, aspect } = calculateOverviewFrustum();
        const halfHeight = frustumSize / 2;
        const halfWidth = frustumSize * aspect / 2;
        
        // If we're not zoomed in, update the camera immediately
        if (this.state !== SheetState.ZOOMED_IN && this.state !== SheetState.DETAIL_VIEW) {
            this.camera.left = -halfWidth;
            this.camera.right = halfWidth;
            this.camera.top = halfHeight;
            this.camera.bottom = -halfHeight;
            this.camera.updateProjectionMatrix();
            
            // Update the stored original frustum
            this.originalFrustum.left = -halfWidth;
            this.originalFrustum.right = halfWidth;
            this.originalFrustum.top = halfHeight;
            this.originalFrustum.bottom = -halfHeight;
        } else {
            // If zoomed in, we should maintain the zoom level but adjust for the new aspect ratio
            const { size, aspect: newAspect } = calculateZoomFrustum();
            const halfSize = size / 2;
            
            this.camera.left = -halfSize * newAspect;
            this.camera.right = halfSize * newAspect;
            this.camera.top = halfSize;
            this.camera.bottom = -halfSize;
            this.camera.updateProjectionMatrix();
            
            // Still update the original frustum for when we zoom out
            this.originalFrustum.left = -halfWidth;
            this.originalFrustum.right = halfWidth;
            this.originalFrustum.top = halfHeight;
            this.originalFrustum.bottom = -halfHeight;
        }
    }
    
    // Set image brightness to highlight the active image
    setImageBrightness(activeRow, activeCol) {
        // Use SheetAnimation's centralized brightness management with mesh cache
        this.animation.setImageBrightness(this.scene, activeRow, activeCol, this.meshCache);
        // NavigationController now handles close button visibility
    }
    
    // Restore all images to full brightness
    resetImageBrightness() {
        this.animation.resetImageBrightness(this.scene, this.meshCache);
    }
    
    // Zoom to a specific image
    zoomToImage(imagePos, row, col) {
        return new Promise(resolve => {
            const isSubsequentMovement = this.state === SheetState.ZOOMED_IN;
            this.state = SheetState.ANIMATING;
            if (this.navigationController) this.navigationController.isTransitioning = true;

            this.setImageBrightness(row, col);

            const onZoomComplete = () => {
                this.navigationController?.updatePosition(row, col);
                
                // Use proper state transition to trigger tutorial
                if (!isSubsequentMovement) {
                    // First time zooming in - use transition method to trigger tutorial
                    this.transitionToZoomedIn();
                } else {
                    // Subsequent movement - just update state directly
                    this.state = SheetState.ZOOMED_IN;
                    this.navigationController?.updateState('ZOOMED_IN');
                }
                
                // Only clear isTransitioning if we're NOT continuing to detail view
                if (!this.shouldShowDetailAfterZoom) {
                    if (this.navigationController) this.navigationController.isTransitioning = false;
                }
                this.updateContextMenuState();
                this.validateStateSync();

                if (this.shouldShowDetailAfterZoom) {
                    this.shouldShowDetailAfterZoom = false;
                    console.log('NC: Step 3/3: detail-view');
                    this.showDetailView().then(resolve);
                } else {
                    resolve();
                }
            };

            this.animation.zoomToImage(imagePos, isSubsequentMovement, onZoomComplete);
        });
    }
    
    // Create images from sheet
    async createImagesFromSheet() {
        try {
            // Get full sheet data with position information
            const { getSheetData } = await import('/src/config/SheetConfig.js');
            const sheetData = await getSheetData(this.sheetId);
            
            if (!sheetData || !sheetData.images) {
                throw new Error(`No sheet data found for ${this.sheetId}`);
            }
            
            const images = sheetData.images;
            
            // Create a reusable geometry for all images
            const imageDimensions = {
                width: this.layout.imageWidth * this.layout.scale,
                height: this.layout.imageHeight * this.layout.scale
            };
            
            // Initialize imageMapping grid
            this.imageMapping = [];
            for (let row = 0; row < this.layout.rows; row++) {
                this.imageMapping[row] = [];
            }
            
            // Clear existing meshes to prevent overlapping
            clearExistingImageMeshes(this.scene, this.sheet, this.SHEET_Z_POSITION);
            
            // Load and create images based on their position data
            const loadingPromises = [];
            let successCount = 0;
            let errorCount = 0;
            let visibleCount = 0;
            const totalImages = images.length;
            const loadingText = document.getElementById('loading-text');
            
            for (const imageData of images) {
                const { filename, position } = imageData;
                const { row, col } = position;
                
                // Store filename in mapping grid
                this.imageMapping[row][col] = filename;
                
                const texturePath = `/images/${this.sheetId}/${filename}`;
                
                // Create a promise for loading this image with correct settings
                const loadPromise = this.imageLoader.loadTextureWithProperEncoding(texturePath)
                    .then(async (texture) => {
                        // Create material and start from black for "developing" effect
                        const material = new THREE.MeshBasicMaterial({
                            map: texture,
                            side: THREE.FrontSide,
                            transparent: true,
                            color: new THREE.Color(0, 0, 0)
                        });

                        const gridPosition = this.layout.getImagePosition(row, col);
                        const mesh = geometryPool.createPlaneMesh(
                            imageDimensions.width,
                            imageDimensions.height,
                            material,
                            { x: gridPosition.x, y: gridPosition.y, z: this.SHEET_Z_POSITION + 0.01 }
                        );

                        // Store reference to row/col in the mesh for later use
                        mesh.userData = { row, col, filename, isSheetImage: true, isDeveloping: true };

                        // Cache mesh for performance - O(1) lookup instead of scene.traverse()
                        this.meshCache.set(`${row},${col}`, mesh);

                        // Add to scene — image is now visible
                        this.scene.add(mesh);

                        // Update loading progress AFTER mesh is in scene
                        visibleCount++;
                        if (loadingText) {
                            loadingText.textContent = `Developing negatives: ${visibleCount}/${totalImages}`;
                        }

                        // Animate brightness from black to full to simulate photo developing
                        gsap.to(material.color, {
                            r: 1,
                            g: 1,
                            b: 1,
                            duration: 1.0,
                            ease: "power2.out",
                            overwrite: 'auto',
                            onComplete: () => { if (mesh && mesh.userData) mesh.userData.isDeveloping = false; }
                        });
                        successCount++;

                        // Cache DOM image for instant DetailView display (after mesh is visible)
                        await this.imageLoader.getDOMImageFromTexture(texturePath);
                    })
                    .catch(async (error) => {
                        // Failed to load image, create a fallback
                        errorCount++;
                        // Cache DOM image for fallback
                        await this.imageLoader.getDOMImageFromTexture(texturePath).catch(() => {});
                        // Create a fallback colored material instead
                        const fallbackMaterial = new THREE.MeshBasicMaterial({
                            color: 0x000000,
                            transparent: true,
                            opacity: 0.8
                        });

                        const gridPosition = this.layout.getImagePosition(row, col);
                        const mesh = geometryPool.createPlaneMesh(
                            imageDimensions.width,
                            imageDimensions.height,
                            fallbackMaterial,
                            { x: gridPosition.x, y: gridPosition.y, z: this.SHEET_Z_POSITION + 0.01 }
                        );
                        mesh.userData = { row, col, filename, isPlaceholder: true, isSheetImage: true, isDeveloping: true };

                        // Cache fallback mesh for performance
                        this.meshCache.set(`${row},${col}`, mesh);

                        this.scene.add(mesh);

                        // Update loading progress AFTER mesh is in scene
                        visibleCount++;
                        if (loadingText) {
                            loadingText.textContent = `Developing negatives: ${visibleCount}/${totalImages}`;
                        }

                        // Animate fallback from black to intended gray to preserve effect
                        const targetGray = new THREE.Color(0x333333);
                        gsap.to(fallbackMaterial.color, {
                            r: targetGray.r,
                            g: targetGray.g,
                            b: targetGray.b,
                            duration: 0.8,
                            ease: "power2.out",
                            overwrite: 'auto',
                            onComplete: () => { if (mesh && mesh.userData) mesh.userData.isDeveloping = false; }
                        });
                    });
                
                loadingPromises.push(loadPromise);
            }
            
            // Wait for all images to load (but don't fail if some images fail)
            await Promise.all(loadingPromises);
            
            // Log that imageMapping is ready
            console.log('ContactSheet: Image mapping ready');
            
            // Log results but don't throw - allow navigation even if some images failed
            if (successCount === 0 && errorCount > 0) {
                console.error(`Failed to load any images for sheet "${this.sheetId}"`);
            } else if (errorCount > 0) {
                console.warn(`Loaded ${successCount} images, ${errorCount} failed for sheet "${this.sheetId}"`);
            }
            
        } catch (error) {
            // Failed to create images
            throw error;
        }
    }
    
    // Zoom out to show the entire sheet
    zoomOut() {
        return new Promise(resolve => {
            this.state = SheetState.ANIMATING;
            if (this.navigationController) this.navigationController.isTransitioning = true;

            // Exit rapid navigation mode if active
            if (this.navigationController && this.navigationController.rapidNavigationMode) {
                this.navigationController.exitRapidNavigationMode();
            }

            // Kill ALL animations before starting zoom out to prevent conflicts
            gsap.killTweensOf(this.camera.position);
            gsap.killTweensOf(this.camera);
            if (this.gradientBackground) {
                gsap.killTweensOf(this.gradientBackground.position);
            }

            // Hide zoom icon immediately on desktop
            this.hideZoomIcon();
            // Hide mobile zoom icon immediately on touch devices
            this.hideMobileZoomIcon(true);
            
            gsap.killTweensOf(this.closeButton);
            gsap.to(this.closeButton, {
                opacity: 0,
                duration: 0.3,
                onComplete: () => { this.closeButton.style.pointerEvents = 'none'; }
            });

            this.resetImageBrightness();

            const onZoomOutComplete = () => {
                if (this.navigationController) this.navigationController.isTransitioning = false;
                this.transitionToOverview();
                resolve();
            };

            this.animation.zoomOut(onZoomOutComplete);
        });
    }
    
    // Pinch gesture handlers
    handlePinchStart(event) {
        if (this.state === SheetState.ANIMATING) return;
        
        // Handle pinch in both OVERVIEW and ZOOMED_IN states
        this.isPinching = true;
        this.initialPinchScale = event.scale;
        
        // Update pointer to center of pinch
        this.updatePointerFromTouch(event.center);
        
        if (this.state === SheetState.OVERVIEW) {
            // Don't mark overview tutorial as learned yet - let it show first
            
            // When in overview state, prepare for pinch-to-zoom-in
            const image = this.getImageAtPointer();
            if (image) {
                this.pinchTargetImage = image;
            } else {
                // If not over an image, find nearest image to pinch center
                this.pinchTargetImage = this.findNearestImageToPoint(event.center);
            }
        } else if (this.state === SheetState.ZOOMED_IN) {
            // When zoomed in, handle existing pinch logic
            
            // Force reset any ongoing drag state
            if (this.isDragging) {
                this.isDragging = false;
                
                // Reset camera position if it was moved by panning
                if (this.hasMovedBeyondThreshold) {
                    const imagePos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
                    gsap.to(this.camera.position, {
                        x: imagePos.x,
                        y: imagePos.y,
                        duration: 0.1,
                        ease: "power1.out"
                    });
                }
                
                // Reset cursor style
                const canvas = document.querySelector('canvas');
                if (canvas) {
                    canvas.style.cursor = 'pointer';
                }
            }
            
            // Verify we're over the current active image
            const image = this.getImageAtPointer();
            if (!image || image.row !== this.currentImage.row || image.col !== this.currentImage.col) {
                this.isPinching = false;
            }
        }
    }
    
    handlePinchOut(event) {
        if (!this.isPinching || this.state === SheetState.ANIMATING) return;
        
        // Calculate pinch change relative to initial scale
        const pinchChange = event.scale / this.initialPinchScale;
        
        if (this.state === SheetState.OVERVIEW) {
            // Pinch-to-zoom-in from overview state
            if (pinchChange > 1.3 && this.pinchTargetImage) {
                this.isPinching = false; // Reset to prevent multiple triggers
                
                // Immediately fade out mobile logo when zoom starts (before animation)
                console.log(`ContactSheet: Pinch-out on image (${this.pinchTargetImage.row},${this.pinchTargetImage.col}). Routing to NC.`);
                this.navigationController?.navigate('zoom-to-image', {
                    position: { row: this.pinchTargetImage.row, col: this.pinchTargetImage.col },
                    source: 'pinch'
                });
            }
        } else if (this.state === SheetState.ZOOMED_IN) {
            // Pinch-to-show-detail when already zoomed in
            if (pinchChange > 1.3) {
                this.isPinching = false; // Reset to prevent multiple triggers
                
                // Ensure any pan operation is completely stopped
                this.isDragging = false;
                this.hasMovedBeyondThreshold = false;
                
                // Route through NavigationController for unified handling
                console.log('ContactSheet: Pinch-out detected. Routing to NC.');
                this.navigationController?.navigate('detail-view');
            }
        }
    }

    handlePinchEnd() {
        this.isPinching = false;
    }
    
    // Add pinch-in handler to zoom out
    handlePinchIn(event) {
        if (!this.isPinching || this.state !== SheetState.ZOOMED_IN || this.state === SheetState.ANIMATING) return;
        
        // Calculate pinch change relative to initial scale
        const pinchChange = event.scale / this.initialPinchScale;
        
        // Only trigger zoom out if pinch is significant enough
        if (pinchChange < 0.7) {
            this.isPinching = false; // Reset to prevent multiple triggers
            
            // Ensure any pan operation is completely stopped
            this.isDragging = false;
            this.hasMovedBeyondThreshold = false;
            
            // Route through NavigationController
            console.log('ContactSheet: Pinch-in detected. Routing to NC.');
            this.navigationController?.navigate('zoom-out');
        }
    }
    
    // Find the nearest image to a screen point (for pinch gestures not directly over an image)
    findNearestImageToPoint(screenPoint) {
        // Convert screen coordinates to world coordinates
        const worldPoint = this.screenToWorldCoordinates(screenPoint);
        
        let minDistance = Infinity;
        let nearestImage = { row: 0, col: 0 };
        
        for (let row = 0; row < this.layout.rows; row++) {
            for (let col = 0; col < this.layout.columns; col++) {
                const imagePos = this.layout.getImagePosition(row, col);
                const distance = Math.sqrt(
                    Math.pow(worldPoint.x - imagePos.x, 2) +
                    Math.pow(worldPoint.y - imagePos.y, 2)
                );
                
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestImage = { row, col };
                }
            }
        }
        
        // Only return the nearest image if it's within the click threshold
        if (minDistance <= CLICK_DISTANCE_THRESHOLD) {
            return nearestImage;
        }
        
        return null;
    }
    
    // Convert screen coordinates to world coordinates based on current camera view
    screenToWorldCoordinates(screenPoint) {
        // Convert screen point to normalized device coordinates
        const x = (screenPoint.x / window.innerWidth) * 2 - 1;
        const y = -(screenPoint.y / window.innerHeight) * 2 + 1;
        
        // Calculate world coordinates based on camera frustum and add camera position
        const worldX = x * (this.camera.right - this.camera.left) / 2 + this.camera.position.x;
        const worldY = y * (this.camera.top - this.camera.bottom) / 2 + this.camera.position.y;
        
        return { x: worldX, y: worldY };
    }
    
    // Detect if this is a touch device
    detectTouchDevice() {
        return (('ontouchstart' in window) ||
                (navigator.maxTouchPoints > 0) ||
                (navigator.msMaxTouchPoints > 0));
    }
    
    // Show zoom icon overlay with image dimming (desktop only)
    showZoomIcon() {
        if (this.isTouchDevice || this.state !== SheetState.ZOOMED_IN || this.isShowingZoomIcon) return;
        
        this.isShowingZoomIcon = true;
        
        // Dim the focused image and show icon simultaneously
        this.dimFocusedImage(true);
        
        // Ensure clean state before animating
        gsap.killTweensOf(this.zoomIconOverlay);
        gsap.set(this.zoomIconOverlay, { opacity: 0 });
        gsap.to(this.zoomIconOverlay, {
            opacity: 1,
            duration: 0.2,
            ease: "power2.out"
        });
    }
    
    // Show zoom icon on mobile as tutorial hint (mobile only)
    showMobileZoomIcon() {
        // Check if this is a deep link to detail view - if so, mark tutorial as learned immediately
        if (window.location.pathname.includes('/detail')) {
            this.tutorialState.mobileZoomIconLearned = true;
        }
        
        if (!this.isTouchDevice || this.state !== SheetState.ZOOMED_IN || this.isShowingZoomIcon || this.tutorialState.mobileZoomIconLearned) return;
        
        this.isShowingZoomIcon = true;
        
        gsap.to(this.zoomIconOverlay, {
            opacity: 0.8,
            duration: 0.3,
            ease: "power2.out"
        });
    }
    
    // Hide zoom icon overlay and restore image brightness
    hideZoomIcon() {
        if (this.isTouchDevice || !this.isShowingZoomIcon) return;
        
        this.isShowingZoomIcon = false;
        
        // Restore focused image brightness and hide icon simultaneously
        this.dimFocusedImage(false);
        
        gsap.killTweensOf(this.zoomIconOverlay);
        gsap.to(this.zoomIconOverlay, {
            opacity: 0,
            duration: 0.2,
            ease: "power2.out"
        });
    }
    
    // Hide mobile zoom icon (mobile only)
    hideMobileZoomIcon(instant = false) {
        if (!this.isTouchDevice) return;
        this.isShowingZoomIcon = false;
        
        // Kill any running tween to prevent pausing mid-animation (e.g., during share sheet)
        gsap.killTweensOf(this.zoomIconOverlay);
        if (instant) {
            // Force immediate hide with no animation
            if (this.zoomIconOverlay) this.zoomIconOverlay.style.opacity = '0';
            return;
        }
        gsap.to(this.zoomIconOverlay, {
            opacity: 0,
            duration: 0.2,
            ease: "power2.out"
        });
    }
    
    // Dim or restore the focused image - O(1) mesh cache lookup
    dimFocusedImage(shouldDim) {
        if (this.state !== SheetState.ZOOMED_IN) return;
        
        // O(1) direct mesh lookup instead of O(n) scene traversal
        const meshKey = `${this.currentImage.row},${this.currentImage.col}`;
        const mesh = this.meshCache.get(meshKey);
        
        if (mesh && mesh.material) {
            // Kill any existing color animations to prevent conflicts
            gsap.killTweensOf(mesh.material.color);
            
            const targetColor = shouldDim ? 0.3 : 1.0;
            gsap.to(mesh.material.color, {
                r: targetColor,
                g: targetColor,
                b: targetColor,
                duration: 0.2,
                ease: "power2.out",
                overwrite: 'auto'
            });
        }
    }
    
    showBounceAnimation(direction) {
        const currentPos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
        const bounceDistance = 0.1; // Small bounce distance
        const bounceDuration = 0.15;
        
        let bounceTarget = { x: currentPos.x, y: currentPos.y };
        
        // Calculate bounce direction
        switch (direction) {
            case 'ArrowUp':
                bounceTarget.y += bounceDistance;
                break;
            case 'ArrowDown':
                bounceTarget.y -= bounceDistance;
                break;
            case 'ArrowLeft':
                bounceTarget.x -= bounceDistance;
                break;
            case 'ArrowRight':
                bounceTarget.x += bounceDistance;
                break;
        }
        
        // Kill existing camera animations to prevent conflicts
        gsap.killTweensOf(this.camera.position);
        
        const tl = gsap.timeline();
        
        // Bounce out
        tl.to(this.camera.position, {
            x: bounceTarget.x,
            y: bounceTarget.y,
            duration: bounceDuration,
            ease: "power2.out",
            overwrite: 'auto'
        })
        // Spring back
        .to(this.camera.position, {
            x: currentPos.x,
            y: currentPos.y,
            duration: bounceDuration * 1.5,
            ease: "power2.out",
            overwrite: 'auto'
        });
        
        // Animate gradient background with parallax if it exists
        if (this.gradientBackground) {
            // Kill existing gradient animations to prevent conflicts
            gsap.killTweensOf(this.gradientBackground.position);
            
            const parallaxFactor = 0.15;
            const gradientBounceTarget = {
                x: bounceTarget.x * parallaxFactor,
                y: bounceTarget.y * parallaxFactor
            };
            const gradientCurrentPos = {
                x: currentPos.x * parallaxFactor,
                y: currentPos.y * parallaxFactor
            };
            
            const gradientTl = gsap.timeline();
            gradientTl.to(this.gradientBackground.position, {
                x: gradientBounceTarget.x,
                y: gradientBounceTarget.y,
                duration: bounceDuration,
                ease: "power2.out",
                overwrite: 'auto'
            })
            .to(this.gradientBackground.position, {
                x: gradientCurrentPos.x,
                y: gradientCurrentPos.y,
                duration: bounceDuration * 1.5,
                ease: "power2.out",
                overwrite: 'auto'
            });
        }
    }
    
    // Quick bounce for rapid navigation at boundaries
    showQuickBounce(direction) {
        const currentPos = this.layout.getImagePosition(this.currentImage.row, this.currentImage.col);
        const bounceDistance = 0.05; // Smaller bounce for rapid mode
        const bounceDuration = 0.08; // Much faster
        
        let bounceTarget = { x: currentPos.x, y: currentPos.y };
        
        // Calculate bounce direction
        const directionMap = {
            'up': 'ArrowUp',
            'down': 'ArrowDown',
            'left': 'ArrowLeft',
            'right': 'ArrowRight'
        };
        const arrowKey = directionMap[direction] || direction;
        
        switch (arrowKey) {
            case 'ArrowUp':
            case 'up':
                bounceTarget.y += bounceDistance;
                break;
            case 'ArrowDown':
            case 'down':
                bounceTarget.y -= bounceDistance;
                break;
            case 'ArrowLeft':
            case 'left':
                bounceTarget.x -= bounceDistance;
                break;
            case 'ArrowRight':
            case 'right':
                bounceTarget.x += bounceDistance;
                break;
        }
        
        // Very quick bounce without timeline
        gsap.killTweensOf(this.camera.position);
        gsap.to(this.camera.position, {
            x: bounceTarget.x,
            y: bounceTarget.y,
            duration: bounceDuration,
            ease: "power2.out",
            yoyo: true,
            repeat: 1,
            overwrite: 'auto'
        });
    }
    
    // Instant jump to position for rapid navigation
    instantJumpToPosition(position) {
        const targetPos = this.layout.getImagePosition(position.row, position.col);
        
        // Kill all ongoing animations
        gsap.killTweensOf(this.camera.position);
        gsap.killTweensOf(this.camera);
        if (this.gradientBackground) {
            gsap.killTweensOf(this.gradientBackground.position);
        }
        
        // Instant position update
        this.camera.position.x = targetPos.x;
        this.camera.position.y = targetPos.y;
        
        // Instant gradient update
        if (this.gradientBackground) {
            const parallaxFactor = 0.15;
            this.gradientBackground.position.x = targetPos.x * parallaxFactor;
            this.gradientBackground.position.y = targetPos.y * parallaxFactor;
        }
        
        // Instant brightness update
        this.instantSetImageBrightness(position.row, position.col);
        
        // Hide mobile zoom icon instantly
        this.hideMobileZoomIcon(true);
        
        // Update context menu state
        this.updateContextMenuState();
    }
    
    // Smooth settle after rapid navigation ends
    smoothSettleToPosition(position) {
        const targetPos = this.layout.getImagePosition(position.row, position.col);
        
        // Only animate if not already at the target position
        const tolerance = 0.001;
        const needsAnimation = Math.abs(this.camera.position.x - targetPos.x) > tolerance ||
                              Math.abs(this.camera.position.y - targetPos.y) > tolerance;
        
        if (needsAnimation) {
            // Smooth ease to ensure we're exactly at the right position
            gsap.to(this.camera.position, {
                x: targetPos.x,
                y: targetPos.y,
                duration: 0.3,
                ease: "power2.out",
                overwrite: 'auto'
            });
            
            if (this.gradientBackground) {
                const parallaxFactor = 0.15;
                gsap.to(this.gradientBackground.position, {
                    x: targetPos.x * parallaxFactor,
                    y: targetPos.y * parallaxFactor,
                    duration: 0.35,
                    ease: "power2.out",
                    overwrite: 'auto'
                });
            }
        }
        
        // Smooth brightness transition to ensure correct state
        this.setImageBrightness(position.row, position.col);
        
        // Show mobile zoom icon after settling (only on touch devices)
        if (this.isTouchDevice) {
            setTimeout(() => {
                this.showMobileZoomIcon();
            }, 300);
        }
    }
    
    // Instant brightness update without animations
    instantSetImageBrightness(activeRow, activeCol) {
        // Kill any ongoing brightness animations
        if (this.animation && this.animation.brightnessTimer) {
            clearTimeout(this.animation.brightnessTimer);
            this.animation.brightnessTimer = null;
        }
        
        // Use mesh cache for O(1) performance
        if (this.meshCache) {
            for (let row = 0; row < 6; row++) {
                for (let col = 0; col < 6; col++) {
                    const meshKey = `${row},${col}`;
                    const mesh = this.meshCache.get(meshKey);
                    
                    if (mesh && mesh.material) {
                        // Kill any existing animations
                        gsap.killTweensOf(mesh.material.color);
                        
                        // Instant color update
                        const isActive = row === activeRow && col === activeCol;
                        if (isActive) {
                            mesh.material.color.r = 1.0;
                            mesh.material.color.g = 1.0;
                            mesh.material.color.b = 1.0;
                        } else {
                            mesh.material.color.r = 0.1;
                            mesh.material.color.g = 0.1;
                            mesh.material.color.b = 0.1;
                        }
                    }
                }
            }
        }
    }

    // State synchronization helpers - ensures ContactSheet and NavigationController stay in sync
    validateStateSync() {
        if (!this.navigationController) return true;
        
        const navState = this.navigationController.state;
        const contactState = this.state;
        
        // Map ContactSheet states to NavigationController states
        const stateMap = {
            [SheetState.OVERVIEW]: 'OVERVIEW',
            [SheetState.ZOOMED_IN]: 'ZOOMED_IN', 
            [SheetState.DETAIL_VIEW]: 'DETAIL_VIEW',
            [SheetState.ANIMATING]: navState // During animation, nav state should remain what it was
        };
        
        const expectedNavState = stateMap[contactState];
        const isInSync = navState === expectedNavState;
        
        if (!isInSync) {
            console.warn(`State sync mismatch: ContactSheet=${contactState}, NavigationController=${navState}`);
        }
        
        return isInSync;
    }
    
    // Centralized state transition helpers - ensures both states update atomically
    transitionToOverview() {
        // Reset gesture state to ensure taps/clicks are always enabled in overview
        this.isDragging = false;
        this.panInProgress = false;
        this.hasMovedBeyondThreshold = false;
        this.swipeDirection = null;
        // Also reset pinch state for safety
        this.isPinching = false;
        
        this.state = SheetState.OVERVIEW;
        this.navigationController?.updateState('OVERVIEW');
        this.updateContextMenuState();
        this.validateStateSync();
        
        // Hide both desktop and mobile zoom icons when returning to overview
        this.hideZoomIcon();
        this.hideMobileZoomIcon();
        
        // Hide tutorial instructions, then show overview tutorial if not learned
        this.hideTutorialInstructions(() => {
            this.showOverviewTutorial();
        });
    }
    
    transitionToZoomedIn() {
        this.state = SheetState.ZOOMED_IN;
        this.navigationController?.updateState('ZOOMED_IN');
        this.updateContextMenuState();
        this.validateStateSync();
        
        // Hide any tutorial instructions when transitioning
        this.hideTutorialInstructions();
        
        // Only show zoomed tutorial if we're not going straight to detail view
        if (!this.shouldShowDetailAfterZoom) {
            setTimeout(() => {
                this.showZoomedTutorial();
                this.showMobileZoomIcon();
            }, 500);
        }
    }
    
    transitionToDetailView() {
        this.state = SheetState.DETAIL_VIEW;
        this.navigationController?.updateState('DETAIL_VIEW');
        this.updateContextMenuState();
        this.validateStateSync();
        
        // Hide mobile zoom icon instantly and mark as learned when entering detail view
        this.hideMobileZoomIcon(true);
        if (this.isTouchDevice && !this.tutorialState.mobileZoomIconLearned) {
            this.tutorialState.mobileZoomIconLearned = true;
        }
        
        // Show detail tutorial if first time (no need to hide first since we're changing content)
        this.showDetailTutorial();
    }
    
    transitionToAnimating() {
        // During animation, preserve the NavigationController's current state
        // Only update ContactSheet state to prevent visual conflicts
        this.state = SheetState.ANIMATING;
        // Don't update NavigationController state during animation
        // validateStateSync will allow this as expected behavior
        this.validateStateSync();
        
        // Hide mobile zoom icon instantly during animation
        this.hideMobileZoomIcon(true);
        
        // Hide any tutorial instructions during animation
        this.hideTutorialInstructions();
    }
    
    // Get image name by position (used by NavigationController for routing)
    getImageNameByPosition(row, col) {
        if (!this.imageMapping || !this.imageMapping[row] || !this.imageMapping[row][col]) {
            return null;
        }
        return this.imageMapping[row][col];
    }

    // Create single tutorial instruction element
    createTutorialInstructions() {
        this.tutorialElement = document.createElement('div');
        this.tutorialElement.style.cssText = `
            position: fixed;
            left: 50%;
            bottom: 0;
            transform: translateX(-50%);
            width: max-content;
            max-width: 90vw;
            padding: 10px 16px;
            background: rgb(0, 0, 0);
            border: 1px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            color: white;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            font-size: 14px;
            text-align: center;
            line-height: 1.5;
            box-shadow: 0 0 10px 10px rgba(0, 0, 0, 0.8);
            opacity: 0;
            pointer-events: none;
            z-index: 1002;
        `;

        document.body.appendChild(this.tutorialElement);

        // Tutorial state tracking - session-based only, no localStorage
        this.tutorialState = {
            overviewLearned: false,
            zoomedLearned: false,
            detailLearned: false,
            mobileZoomIconLearned: false
        };
    }

    markTutorialLearned(type) {
        this.tutorialState[type + 'Learned'] = true;
        // Removed localStorage persistence - tutorials show fresh each session
    }

    showTutorial(text, bottom, type) {
        if (this.tutorialState[type + 'Learned']) return;
        
        // Store current tutorial type for interaction detection
        this.currentTutorialType = type;
        
        gsap.killTweensOf(this.tutorialElement);
        
        const tl = gsap.timeline();
        tl.to(this.tutorialElement, { opacity: 0, duration: 0.2 })
          .call(() => {
              this.tutorialElement.innerHTML = text;
              this.tutorialElement.style.bottom = bottom;
          })
          .to(this.tutorialElement, { opacity: 0.9, duration: 0.5 });
          // Remove auto-fade - tutorial stays visible until interaction
    }

    // Simple method for NavigationController to mark tutorial as learned
    markCurrentTutorialLearned() {
        if (this.currentTutorialType && !this.tutorialState[this.currentTutorialType + 'Learned']) {
            this.markTutorialLearned(this.currentTutorialType);
            this.hideTutorialInstructions();
            this.currentTutorialType = null;
        }
    }

    showOverviewTutorial() {
        if (this.state !== SheetState.OVERVIEW) return;
        const message = this.isTouchDevice ? 
            'Tap any image or pinch to zoom in' : 
            'Click any image or use ENTER key to zoom in';
        this.showTutorial(message, '40px', 'overview');
    }

    showZoomedTutorial() {
        if (this.state !== SheetState.ZOOMED_IN) return;
        const fourArrowsSVG = '<svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 45.41 45.41" width="1em" height="1em" style="display:inline;vertical-align:middle;margin:0 0.05em;"><defs><style>.st0 {fill: #fff;}</style></defs><polygon class="st0" points="36.71 14 34.58 16.1 39.67 21.21 24.21 21.21 24.21 5.73 29.31 10.84 31.41 8.7 22.71 0 14 8.7 16.1 10.84 21.21 5.73 21.21 21.21 5.74 21.21 10.84 16.1 8.7 14 0 22.71 8.7 31.41 10.84 29.31 5.74 24.21 21.21 24.21 21.21 39.68 16.1 34.57 14 36.71 22.71 45.41 31.41 36.71 29.31 34.57 24.21 39.68 24.21 24.21 39.67 24.21 34.58 29.31 36.71 31.41 45.41 22.71 36.71 14"/></svg>';
        const message = this.isTouchDevice ? 
            `Swipe ${fourArrowsSVG} to navigate • Tap&nbsp;for&nbsp;detail` : 
            'Click or use keyboard arrows to navigate • Tap&nbsp;in&nbsp;for&nbsp;detail';
        this.showTutorial(message, '50px', 'zoomed');
    }

    showDetailTutorial() {
        if (this.state !== SheetState.DETAIL_VIEW) return;
        const closeBtnSVG = '<svg width="12" height="12" viewBox="0 0 24 24" style="display:inline;vertical-align:middle;" xmlns="http://www.w3.org/2000/svg"><line x1="6" y1="6" x2="18" y2="18" stroke="white" stroke-width="3" stroke-linecap="round"/><line x1="18" y1="6" x2="6" y2="18" stroke="white" stroke-width="3" stroke-linecap="round"/></svg>';
        const arrowRangeSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" width="1em" height="1em" style="display:inline;vertical-align:middle;margin:0 0.05em;" fill="#fff"><path d="M280-280 80-480l200-200 56 56-103 104h494L624-624l56-56 200 200-200 200-56-56 103-104H233l103 104-56 56Z"/></svg>';
        const message = this.isTouchDevice ? 
            `Swipe ${arrowRangeSVG} to navigate or scroll &#8597; for detail • Tap&nbsp;${closeBtnSVG}&nbsp;to&nbsp;close` : 
            `Use &#8249;&#8250; buttons to navigate or scroll &#8597; for detail • Click&nbsp;${closeBtnSVG}&nbsp;to&nbsp;close`;
        this.showTutorial(message, '60px', 'detail');
    }

    hideTutorialInstructions(onComplete = null) {
        if (this.tutorialTimeout) clearTimeout(this.tutorialTimeout);
        gsap.killTweensOf(this.tutorialElement);
        gsap.to(this.tutorialElement, { 
            opacity: 0, 
            duration: 0.3, 
            ease: "power2.out",
            onComplete: onComplete
        });
    }
} 